import React, { useState } from 'react';
import Weblayout from './Weblayout';
import PageTransition from '../components/PageTransition';
import { motion, AnimatePresence } from 'framer-motion';
import { savesupport } from '../components/DbServices';
import Swal from 'sweetalert2'; // Use 'Swal' for modern SweetAlert2

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await savesupport(formData);

      // result?.status === 200 matches your service response logic
      if (result?.status === 200) {
        setSubmitted(true);
        setFormData({ name: '', email: '', description: '' });
        
        Swal.fire({
          title: "Success",
          text: "Your Ticket Has Been Submitted Successfully.",
          icon: "success",
          confirmButtonColor: "#6366f1", // Matches your primary theme
          borderRadius: "15px"
        });
      } else {
        Swal.fire({
          title: "Error",
          text: result?.message || "An unexpected error occurred. Please try again later.",
          icon: "error",
          confirmButtonColor: "#000"
        });
      }
    } catch (error) {
      Swal.fire({
        title: "System Error",
        text: "Could not connect to the server.",
        icon: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      <Weblayout isLegal={true}>
        <motion.div 
          className="bento-item p-0 border-0 bg-white overflow-hidden d-flex flex-column h-100 shadow-lg"
          style={{ borderRadius: '24px' }}
        >
          {/* HEADER SECTION */}
          <div className="bg-light border-bottom p-4 flex-shrink-0 d-flex justify-content-between align-items-center">
            <div>
              <h2 className="fw-black text-dark m-0">Support Ticket</h2>
              <small className="text-muted font-monospace text-uppercase">Contact Support</small>
            </div>
            <div className={`badge ${submitted ? 'bg-success' : 'bg-primary'} bg-opacity-10 ${submitted ? 'text-success' : 'text-primary'} border px-3 py-2`}>
              <i className={`bi ${submitted ? 'bi-check-all' : 'bi-cpu-fill'} me-1`}></i> 
              {submitted ? 'SIGNAL_RECEIVED' : 'NODE_READY'}
            </div>
          </div>

          {/* SCROLLABLE CONTENT BODY */}
          <div className="p-4 p-md-5 overflow-auto text-dark privacy-content flex-grow-1">
            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.div 
                  key="form"
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  exit={{ opacity: 0, y: -20 }}
                  className="row g-5"
                >
                  <div className="col-lg-7">
                    <section>
                      <h6 className="font-monospace text-primary fw-bold mb-2 text-uppercase">01_Initialize_Inquiry</h6>
                      <h4 className="fw-black text-dark mb-4 tracking-tight">Open Support Channel</h4>
                      
                      <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                          <label className="form-label small fw-bold text-uppercase text-muted tracking-wider">Full Name</label>
                          <input 
                            type="text" 
                            className="form-control form-control-lg bg-light border-0 rounded-4 px-3" 
                            placeholder="John Doe"
                            required
                            disabled={loading}
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label small fw-bold text-uppercase text-muted tracking-wider">System Email</label>
                          <input 
                            type="email" 
                            className="form-control form-control-lg bg-light border-0 rounded-4 px-3" 
                            placeholder="john@company.com"
                            required
                            disabled={loading}
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                          />
                        </div>

                        <div className="mb-4">
                          <label className="form-label small fw-bold text-uppercase text-muted tracking-wider">Issue Description</label>
                          <textarea 
                            className="form-control form-control-lg bg-light border-0 rounded-4 px-3" 
                            rows="4" 
                            placeholder="Describe the technical requirements or issues..."
                            required
                            disabled={loading}
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                          ></textarea>
                        </div>

                        <button 
                          type="submit" 
                          disabled={loading}
                          className="btn btn-primary btn-lg w-100 rounded-pill fw-black py-3 shadow-sm d-flex align-items-center justify-content-center gap-2"
                        >
                          {loading ? (
                            <><span className="spinner-border spinner-border-sm"></span> PROCESSING...</>
                          ) : (
                            <>SUBMIT <i className="bi bi-chevron-right"></i></>
                          )}
                        </button>
                      </form>
                    </section>
                  </div>

                  <div className="col-lg-5">
                    <section className="h-100 d-flex flex-column">
                      <h6 className="font-monospace text-primary fw-bold mb-2">02_LOCATION_DATA</h6>
                      <h4 className="fw-black text-dark mb-4">Contact Nodes</h4>
                      <div className="p-4 rounded-4 border bg-light flex-grow-1">
                        <p className="small text-muted mb-4 font-monospace">// ENCRYPTED_ENDPOINT</p>
                        <p className="m-0 fw-bold">Global Support HQ</p>
                        <p className="text-muted small mb-4 tracking-tighter">Sector 44, Gurugram, India</p>
                        
                        <div className="d-flex flex-column gap-2 mt-auto">
                          <div className="p-2 bg-white rounded-3 border-sm small">
                            <strong>MAIL:</strong> support@yourdomain.com
                          </div>
                          <div className="p-2 bg-white rounded-3 border-sm small">
                            <strong>WA:</strong> +91 98765 43210
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </motion.div>
              ) : (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }} 
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-5 my-5"
                >
                  <div className="display-1 text-success mb-4"><i className="bi bi-check-circle-fill"></i></div>
                  <h2 className="fw-black text-dark">Signal Transmitted!</h2>
                  <p className="text-muted mx-auto" style={{ maxWidth: '400px' }}>
                    Your inquiry has been logged in our system. A support engineer will verify the payload and respond within the SLA window.
                  </p>
                  <button 
                    className="btn btn-outline-dark rounded-pill px-5 fw-bold mt-4"
                    onClick={() => setSubmitted(false)}
                  >
                    OPEN_NEW_TICKET
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </Weblayout>
    </PageTransition>
  );
}